import useIsTextTruncated from '@app/src/hooks/useIsTextTruncated'
import type { TypographyProps } from '@mui/material'
import { Tooltip, Typography } from '@mui/material'
import React from 'react'

type TruncatingTextProps = { text: string; children?: React.ReactNode } & Omit<
  TypographyProps,
  'children' | 'noWrap' | 'component'
>

// Single-line text that ellipsis-truncates and reveals the full value in a tooltip only when it is
// actually clipped. Pass `children` to truncate arbitrary inline content (e.g. a link); otherwise the
// text renders directly. This is the shared building block for truncating table cells so they don't each
// reimplement the measure-and-tooltip dance.
const TruncatingText: React.FC<TruncatingTextProps> = ({ text, children, sx, ...props }) => {
  const { ref, isTruncated } = useIsTextTruncated<HTMLSpanElement>(text)

  // The Tooltip stays mounted and only its title toggles. Wrapping the Typography conditionally would
  // remount it whenever truncation is detected, detaching the ref/ResizeObserver that measure overflow —
  // the observer then fires on the removed node (0×0), resetting isTruncated to false and hiding the
  // tooltip for good. Keeping the tree stable lets the measurement survive the state change.
  // {...props} first so callers can't override the settings truncation depends on (component/noWrap/ref).
  return (
    <Tooltip title={isTruncated ? text : ''} disableHoverListener={!isTruncated}>
      <Typography
        {...props}
        ref={ref}
        component="span"
        noWrap
        sx={[{ display: 'block', minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx ?? false])]}
      >
        {children ?? text}
      </Typography>
    </Tooltip>
  )
}

export default TruncatingText
